import { MouseEvent, ReactNode, useState } from 'react';

import { InvitationRateLimitState, Workspace, WorkspaceInput, WorkspaceUsers } from '../../mainApi';
import { WorkspacesService } from '../../services/workspaces.service';
import { MainFilter } from '../../stores/mainStore';
import { WorkspaceDialogState } from '../../types';
import { WorkspaceMenu } from './WorkspaceMenu';
import { WorkspaceDialog } from './WorkspaceDialog';
import { WorkspaceMembersDialog } from './members/WorkspaceMembersDialog';

type WorkspaceControlsRenderProps = {
  canOpenWorkspaceMenu: boolean;
  memberCount: number | null;
  onCreateWorkspace: () => void;
  onWorkspaceMenuOpen: (event: MouseEvent<HTMLElement>) => void;
  onWorkspaceMembersOpen: () => void;
};

type WorkspaceControlsProps = {
  filter: MainFilter;
  selectedWorkspaceId: string | null;
  selectedWorkspace: Workspace | null;
  isSaving: boolean;
  addWorkspace: (input: WorkspaceInput) => Promise<void>;
  editWorkspace: (workspaceId: string, input: WorkspaceInput) => Promise<void>;
  removeWorkspace: (workspaceId: string, withSpaces: boolean) => Promise<void>;
  loadWorkspaces: () => Promise<void>;
  selectHome: () => void;
  children: (props: WorkspaceControlsRenderProps) => ReactNode;
};

export const WorkspaceControls = ({
  filter,
  selectedWorkspaceId,
  selectedWorkspace,
  isSaving,
  addWorkspace,
  editWorkspace,
  removeWorkspace,
  loadWorkspaces,
  selectHome,
  children,
}: WorkspaceControlsProps) => {
  const [workspaceDialog, setWorkspaceDialog] = useState<WorkspaceDialogState>({
    mode: 'create',
    open: false,
    workspace: null,
  });
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [workspaceVisible, setWorkspaceVisible] = useState(false);
  const [workspaceAuth, setWorkspaceAuth] = useState<'None' | 'Password'>('None');
  const [workspacePassword, setWorkspacePassword] = useState('');
  const [deleteWorkspaceWithSpaces, setDeleteWorkspaceWithSpaces] = useState(false);
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState<null | HTMLElement>(null);
  const [workspaceMembersOpen, setWorkspaceMembersOpen] = useState(false);
  const [workspaceMembersUsers, setWorkspaceMembersUsers] = useState<WorkspaceUsers | null>(null);
  const [workspaceMembersError, setWorkspaceMembersError] = useState<string | null>(null);
  const [isWorkspaceMembersLoading, setIsWorkspaceMembersLoading] = useState(false);
  const [isWorkspaceMembersSaving, setIsWorkspaceMembersSaving] = useState(false);
  const [workspaceInviteEmails, setWorkspaceInviteEmails] = useState('');
  const [workspaceInviteMessage, setWorkspaceInviteMessage] = useState('');
  const [workspaceInviteOpen, setWorkspaceInviteOpen] = useState(false);
  const [invitationRateLimit, setInvitationRateLimit] = useState<InvitationRateLimitState | null>(
    null
  );

  const loadInvitationRateLimit = async () => {
    try {
      setInvitationRateLimit(await WorkspacesService.getInvitationRateLimit());
    } catch {
      setInvitationRateLimit(null);
    }
  };

  const openWorkspaceDialog = (mode: WorkspaceDialogState['mode'], workspace: Workspace | null) => {
    setWorkspaceDialog({ mode, open: true, workspace });
    setWorkspaceTitle(workspace?.title ?? '');
    setWorkspaceVisible(Boolean(workspace?.externallyVisible));
    setWorkspaceAuth((workspace?.authenticationType as 'None' | 'Password' | undefined) ?? 'None');
    setWorkspacePassword('');
    setDeleteWorkspaceWithSpaces(false);
    setWorkspaceMenuAnchor(null);
  };

  const onWorkspaceSubmit = async () => {
    if (!workspaceTitle.trim()) {
      return;
    }

    if (workspaceDialog.mode === 'create') {
      await addWorkspace({
        title: workspaceTitle.trim(),
        externallyVisible: workspaceVisible,
        authenticationType: workspaceVisible ? workspaceAuth : 'None',
        password: workspaceVisible && workspaceAuth === 'Password' ? workspacePassword.trim() : '',
      });
    }

    if (workspaceDialog.mode === 'edit' && workspaceDialog.workspace?.id) {
      await editWorkspace(workspaceDialog.workspace.id, {
        title: workspaceTitle.trim(),
        externallyVisible: workspaceVisible,
        authenticationType: workspaceVisible ? workspaceAuth : 'None',
        password: workspaceVisible && workspaceAuth === 'Password' ? workspacePassword.trim() : '',
      });
    }

    if (workspaceDialog.mode === 'delete' && workspaceDialog.workspace?.id) {
      const removedId = workspaceDialog.workspace.id;
      await removeWorkspace(removedId, deleteWorkspaceWithSpaces);
      if (selectedWorkspaceId === removedId) {
        selectHome();
      }
    }

    setWorkspaceDialog((value) => ({ ...value, open: false }));
  };

  const loadWorkspaceMembers = async (workspaceId: string) => {
    setWorkspaceMembersError(null);
    setIsWorkspaceMembersLoading(true);

    try {
      const users = await WorkspacesService.getUsers(workspaceId);
      setWorkspaceMembersUsers({
        owner: users.owner,
        members: users.members ?? [],
        pending: users.pending ?? [],
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load workspace members.';
      setWorkspaceMembersError(message);
      setWorkspaceMembersUsers(null);
    } finally {
      setIsWorkspaceMembersLoading(false);
    }
  };

  const openWorkspaceMembersDialog = async () => {
    if (!selectedWorkspace?.id) {
      return;
    }

    setWorkspaceMenuAnchor(null);
    setWorkspaceInviteEmails('');
    setWorkspaceInviteMessage('');
    setWorkspaceInviteOpen(false);
    setWorkspaceMembersOpen(true);
    await Promise.all([loadWorkspaceMembers(selectedWorkspace.id), loadInvitationRateLimit()]);
  };

  const handleWorkspaceMembersClose = () => {
    setWorkspaceMembersOpen(false);
    setWorkspaceMembersError(null);
    setWorkspaceMembersUsers(null);
    setWorkspaceInviteEmails('');
    setWorkspaceInviteMessage('');
    setWorkspaceInviteOpen(false);
    setInvitationRateLimit(null);
  };

  const handleWorkspaceInvite = async () => {
    if (!selectedWorkspace?.id || !workspaceInviteEmails.trim()) {
      return;
    }

    setWorkspaceMembersError(null);
    setIsWorkspaceMembersSaving(true);
    try {
      const response = await WorkspacesService.invite(
        selectedWorkspace.id,
        workspaceInviteEmails.trim(),
        workspaceInviteMessage.trim()
      );
      setInvitationRateLimit(response.rateLimit);
      setWorkspaceInviteEmails('');
      setWorkspaceInviteMessage('');
      setWorkspaceInviteOpen(false);
      await loadWorkspaceMembers(selectedWorkspace.id);
      await loadWorkspaces();
    } catch (error) {
      await loadInvitationRateLimit();
      const message =
        error instanceof Error ? error.message : 'Unable to send workspace invitations.';
      setWorkspaceMembersError(message);
    } finally {
      setIsWorkspaceMembersSaving(false);
    }
  };

  const handleWorkspaceMemberRemove = async (userId: string) => {
    if (!selectedWorkspace?.id || !userId) {
      return;
    }

    setWorkspaceMembersError(null);
    setIsWorkspaceMembersSaving(true);
    try {
      await WorkspacesService.removeUser(selectedWorkspace.id, userId);
      await loadWorkspaceMembers(selectedWorkspace.id);
      await loadWorkspaces();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to remove workspace member.';
      setWorkspaceMembersError(message);
    } finally {
      setIsWorkspaceMembersSaving(false);
    }
  };

  return (
    <>
      {children({
        canOpenWorkspaceMenu: Boolean(selectedWorkspace?.isOwner),
        memberCount:
          filter === 'workspace' && selectedWorkspace ? selectedWorkspace.memberCount ?? 0 : null,
        onCreateWorkspace: () => openWorkspaceDialog('create', null),
        onWorkspaceMenuOpen: (event) => setWorkspaceMenuAnchor(event.currentTarget),
        onWorkspaceMembersOpen: () => void openWorkspaceMembersDialog(),
      })}

      <WorkspaceMenu
        anchorEl={workspaceMenuAnchor}
        onClose={() => setWorkspaceMenuAnchor(null)}
        onMembers={() => void openWorkspaceMembersDialog()}
        onEdit={() => openWorkspaceDialog('edit', selectedWorkspace)}
        onDelete={() => openWorkspaceDialog('delete', selectedWorkspace)}
      />

      <WorkspaceDialog
        dialogState={workspaceDialog}
        isSaving={isSaving}
        title={workspaceTitle}
        externallyVisible={workspaceVisible}
        authType={workspaceAuth}
        password={workspacePassword}
        deleteWithSpaces={deleteWorkspaceWithSpaces}
        onClose={() => setWorkspaceDialog((value) => ({ ...value, open: false }))}
        onSubmit={() => void onWorkspaceSubmit()}
        onTitleChange={setWorkspaceTitle}
        onVisibleChange={setWorkspaceVisible}
        onAuthTypeChange={setWorkspaceAuth}
        onPasswordChange={setWorkspacePassword}
        onDeleteWithSpacesChange={setDeleteWorkspaceWithSpaces}
      />

      <WorkspaceMembersDialog
        open={workspaceMembersOpen}
        users={workspaceMembersUsers}
        isOwner={Boolean(selectedWorkspace?.isOwner)}
        isLoading={isWorkspaceMembersLoading}
        isSaving={isWorkspaceMembersSaving}
        error={workspaceMembersError}
        inviteEmails={workspaceInviteEmails}
        inviteMessage={workspaceInviteMessage}
        inviteOpen={workspaceInviteOpen}
        onClose={handleWorkspaceMembersClose}
        onInviteOpen={() => setWorkspaceInviteOpen(true)}
        onInviteEmailsChange={setWorkspaceInviteEmails}
        onInviteMessageChange={setWorkspaceInviteMessage}
        onInvite={() => void handleWorkspaceInvite()}
        onRemove={(userId) => void handleWorkspaceMemberRemove(userId)}
        invitationRateLimit={invitationRateLimit}
      />
    </>
  );
};
